/**
 * Recursively generate a set of routes that is prepended with "/:locale?"
 * so only one set of routes need to be maintained.
 *
 * Input typical routing patterns from 'react-router-config' as the following:
 *
 * generateLocaleRoutes([
 *   {
 *     component: App,
 *     routes: [
 *       {
 *         path: '/',
 *         exact: true,
 *         component: Home,
 *       },
 *       {
 *         path: '/login',
 *         exact: true,
 *         component: Login,
 *       }
 *     ]
 *   }
 * ])
 *
 * It outputs the result of:
 *
 * [
 *   component: App,
 *   routes: [
 *     {
 *       path: '/:locale?'
 *       exact: true,
 *       component: Home
 *     },
 *     {
 *       path: '/:locale?/login'
 *       exact: true,
 *       component: Login,
 *     }
 *   ]
 * ]
 */

/**
 * Responsible for mutating origin route.
 *
 * @param {Object} [route]
 * @param {String} [prependPattern]
 * @param {Array} [mutators]
 * @param {depth} [number]
 */
export const mutateRoute = (route, prependPattern, mutators, depth) => {
  const { path } = route;

  const trimTrailingSlash = string => string.replace(/\/$/, '');

  const appendedPath = path
    ? trimTrailingSlash(`${prependPattern}${path}`)
    : undefined;

  let localeRoute = {
    ...route,
    path: appendedPath,
  };

  // locate the mutator that is matching both depth and urlMatcher
  const routeMutator = mutators.find(
    mutator => mutator.depth === depth && mutator.pathMatcher === appendedPath
  );

  // if mutator exists we mutate the current route.
  if (routeMutator) {
    const { callback } = routeMutator;

    if (callback) {
      localeRoute = callback(localeRoute);
    }
  }

  return localeRoute;
};

let depth = 0;
export default function generateLocaleRoutes(
  origRoutes,
  prependPattern = '',
  mutators = []
) {
  const localeRoutes = [];
  // loop through each route.
  origRoutes.forEach(route => {
    // retrieve routes from normal route.
    const { routes } = route;

    // if route contains "routes" attribute, plug the route array back to generateLocaleRoutes.
    // deep appending locale.
    if (routes && routes.length > 0) {
      const localeRoute = mutateRoute(route, prependPattern, mutators, depth);

      // increment recursive depth.
      depth++;

      localeRoutes.push({
        ...localeRoute,
        routes: generateLocaleRoutes(routes, prependPattern, mutators),
      });
    } else {
      const localeRoute = mutateRoute(route, prependPattern, mutators, depth);

      localeRoutes.push(localeRoute);
    }
  });

  return localeRoutes;
}



// WEBPACK FOOTER //
// ./app/utils/routesGenerator.js